import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Button } from '../../components/ui/Button'
import { Field, Notice } from '../../components/ui/misc'
import { useConfig } from '../../lib/api'
import { authClient } from '../../lib/auth'
import { AuthLayout, Divider, GoogleButton, authErrorMessage } from './AuthLayout'

export function safeNext(next: string | null): string {
  return next && next.startsWith('/') && !next.startsWith('//') ? next : '/home'
}

export function LoginPage() {
  const [params] = useSearchParams()
  const next = safeNext(params.get('next'))
  const { data: config } = useConfig()
  const [email, setEmail] = useState(params.get('email') ?? '')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(
    params.get('error') ? authErrorMessage({ message: params.get('error') ?? undefined, code: params.get('error') ?? undefined }) : null,
  )
  const navigate = useNavigate()
  const qc = useQueryClient()

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    const { error: err } = await authClient.signIn.email({ email, password })
    setBusy(false)
    if (err) return setError(authErrorMessage(err))
    await qc.invalidateQueries()
    navigate(next)
  }

  const google = () =>
    authClient.signIn.social({
      provider: 'google',
      callbackURL: next,
      errorCallbackURL: `/login?next=${encodeURIComponent(next)}`,
    })

  return (
    <AuthLayout
      title="Sign in"
      subtitle="Welcome back."
      footer={
        <>
          New here?{' '}
          {config?.signupMode === 'open' ? (
            <Link className="font-medium text-brand" to={`/signup?next=${encodeURIComponent(next)}`}>
              Create an account
            </Link>
          ) : (
            <Link className="font-medium text-brand" to="/waitlist">
              Join the waitlist
            </Link>
          )}
        </>
      }
    >
      {error && (
        <div className="mb-4">
          <Notice tone="warning">{error}</Notice>
        </div>
      )}
      {config?.features.google && (
        <>
          <GoogleButton onClick={google} />
          <Divider />
        </>
      )}
      <form onSubmit={submit} className="space-y-3">
        <Field label="Email">
          <input className="input" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field label="Password">
          <input className="input" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>
        <div className="flex justify-end">
          <Link to="/forgot-password" className="text-[12px] text-ink-muted hover:text-ink">
            Forgot password?
          </Link>
        </div>
        <Button type="submit" className="h-10 w-full" loading={busy}>
          Sign in
        </Button>
      </form>
    </AuthLayout>
  )
}
